import {
  Avatar,
  ChannelIcon,
  Drawer,
  Flex,
  IconButton,
  Text,
  XIcon,
  useIsMobileScreenSize,
} from '@buffer-mono/popcorn'

import { isChannelIconService } from '../../util/isChannelIconService'
import { getCounterpart } from '../ConversationCard/conversationDisplay'
import type { ConversationCardData } from '../fragments/conversationCard'

import { REPLAY_BLOCK_CLASS } from '../replayPrivacy'

import styles from './DetailsPanel.module.css'

import type { JSX } from 'react'

type DetailsPanelProps = {
  open: boolean
  onClose: () => void
  /** The open conversation; the pane describes the person it is with. */
  conversation: ConversationCardData | undefined
}

/**
 * Right pane of the Messages view, per the DMs Figma "Previous interactions"
 * frame: who the conversation is with, as a card beside the thread.
 *
 * Only the profile block renders. The design continues with the person's
 * past comments and mentions, which need data the API does not return yet,
 * so the card stops after the handle rather than show an empty section.
 *
 * Not built on `Sidepanel`: that component is a bordered list panel that pads
 * towards content on its right. This is a sunken card with its own inset,
 * and it has no collapsed state: it is either mounted or not. Below the
 * mobile breakpoint there is no room for a third column, so the same content
 * is presented as a drawer over the thread.
 */
export function DetailsPanel({
  open,
  onClose,
  conversation,
}: DetailsPanelProps): JSX.Element | null {
  const isMobile = useIsMobileScreenSize()

  if (!open || !conversation) return null

  const profile = <Profile conversation={conversation} />

  if (isMobile) {
    return (
      <Drawer
        open
        onOpenChange={(nextOpen): void => {
          if (!nextOpen) onClose()
        }}
        direction="right"
      >
        {/* The drawer renders outside the view, so it is blocked in session
            replay recordings on its own. */}
        <Drawer.Content className={REPLAY_BLOCK_CLASS}>
          <Drawer.Header>
            <Drawer.Title>Conversation details</Drawer.Title>
          </Drawer.Header>
          <Drawer.Body>{profile}</Drawer.Body>
        </Drawer.Content>
      </Drawer>
    )
  }

  return (
    <Flex
      as="aside"
      direction="column"
      gap="lg"
      aria-label="Conversation details"
      className={styles.panel}
    >
      <Flex justify="end" fullWidth>
        <IconButton
          label="Close details"
          variant="tertiary"
          size="small"
          onClick={onClose}
        >
          <XIcon />
        </IconButton>
      </Flex>
      {profile}
    </Flex>
  )
}

/** The person the conversation is with: avatar, name, network and handle. */
function Profile({
  conversation,
}: {
  conversation: ConversationCardData
}): JSX.Element {
  const counterpart = getCounterpart(conversation.participants)
  const service = conversation.serviceType

  return (
    <Flex direction="column" align="center" gap="xs" fullWidth>
      <Avatar
        src={counterpart.avatar}
        alt=""
        fallback="letter"
        fallbackName={counterpart.fallbackName}
        size="large"
      />
      <Flex direction="column" align="center" gap="3xs" className={styles.text}>
        <Text
          size="lg"
          weight="medium"
          lineHeight="tight"
          align="center"
          className={styles.truncated}
        >
          {counterpart.name}
        </Text>
        {counterpart.username && (
          <Flex align="center" gap="2xs">
            {isChannelIconService(service) && (
              <ChannelIcon type={service} size="small" color="subtle" />
            )}
            <Text
              weight="medium"
              color="subtle"
              lineHeight="tight"
              className={styles.truncated}
            >
              {counterpart.username}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
